import React, { useMemo, useState } from 'react';

import { ALINE_TOOL_NAMES, ASK_USER_SKIPPED_ANSWER, isUUIDV4 } from '@aline/common';
import type { AlineToolName, UUID } from '@aline/common';
import { link } from '@aline/routes';
import { ButtonBase, Typography } from '@mui/material';
import * as Sentry from '@sentry/react';
import clsx from 'clsx';
import { format, isValid, parseISO } from 'date-fns';
import { Check } from 'lucide-react';
import { useSnackbar } from 'notistack';
import { AlineInput } from 'src/AlineInput';
import type { DocumentMentionAccessResult } from 'src/components/ai/chat/chat-hooks/useMentionAccessContext';
import { useMentionEntitiesQuery } from 'src/components/ai/chat/chat-hooks/useMentionEntitiesQuery';
import {
  computeDocumentAccessData,
  DocumentMentionChip,
} from 'src/components/ai/chat/ChatPanel/ChatEditor/ChatEditorMention';
import { AlineMarkdown } from 'src/components/ai/chat/ChatPanelMarkdown/AlineMarkdown';
import { useChatAdapter } from 'src/components/ai/chat/ChatPanelMarkdown/ChatPanelMarkdown';
import { UserAutocomplete } from 'src/components/ai/reminder-automation/UserAutocomplete';
import type { UserOption } from 'src/components/ai/reminder-automation/UserAutocomplete';
import { AlineDatePicker } from 'src/components/generic';
import { AlineCheckbox } from 'src/components/generic/AlineCheckbox';
import { getAssignmentUser } from 'src/components/workflow/workflow-detail/side-panel/task-review/task-review-helpers';
import { Apollo } from 'src/gql/generated';
import { openLink } from 'src/helpers';
import { useGetOrgMembers } from 'src/hooks';
import { getTypographyForSx } from 'src/theme';
import { focusFieldControl } from 'src/utils/focus-field-control';
import { getIsPlainRecord } from 'src/utils/get-is-plain-record';
import { makeStyles } from 'tss-react/mui';
import { z } from 'zod';

import { AskUserFileUpload } from './AskUserFileUpload';
import type { AskUserFile } from './AskUserFileUpload';
import {
  CardPager,
  CardPrimaryAction,
  CardSecondaryAction,
  PendingInteractionCardChrome,
} from './PendingInteractionCardChrome';

/* Re-exported so existing importers keep one place to reach the shared shell. */
export {
  CardPager,
  CardPrimaryAction,
  CardSecondaryAction,
  PendingInteractionCardChrome,
} from './PendingInteractionCardChrome';

export interface AskUserOption {
  id: string;
  label: string;
  source: string;
}

/**
 * Single discriminant mirroring askUserQuestionSchema's `type`. Mutually exclusive
 * field kinds stay unrepresentable as overlapping booleans.
 */
export type AskUserQuestionKind =
  | 'date'
  | 'email'
  | 'file'
  | 'multiSelect'
  | 'multiline'
  | 'number'
  | 'select'
  | 'text'
  | 'user';

export interface AskUserQuestion {
  kind: AskUserQuestionKind;
  maxFiles: number | null;
  options: AskUserOption[] | null;
  placeholder: string | null;
  question: string;
  required: boolean;
  suggestedOptionIds: string[] | null;
  suggestedValue: string | null;
}

/**
 * Shown when a pending askUser part can't be parsed (a corrupt or legacy row).
 * A single free-text question keeps the turn answerable instead of leaving it
 * suspended with no card to resolve it.
 */
export const FALLBACK_ASK_USER_QUESTIONS: AskUserQuestion[] = [
  {
    kind: 'text',
    maxFiles: null,
    options: null,
    placeholder: null,
    question: 'Aline needs more information to continue. How would you like to proceed?',
    required: false,
    suggestedOptionIds: null,
    suggestedValue: null,
  },
];

export interface AskUserAnswer {
  answer: string;
  question: string;
  files?: AskUserFile[];
  selectedOption?: AskUserOption;
  selectedOptions?: AskUserOption[];
}

type AskUserDraft =
  | { files: AskUserFile[]; kind: 'file' }
  | { kind: 'multiOption'; options: AskUserOption[] }
  | { kind: 'option'; option: AskUserOption }
  | { kind: 'skipped' }
  | { kind: 'text'; text: string };

/** What a skill proposal card shows, and what approving it would save. */
type SkillProposalApprovalDetails = {
  changeSummary: string | null;
  description: string;
  /** The documents approving would attach, in the order the proposal lists them. */
  documentIds: UUID[];
  instructions: string;
  name: string;
};

/**
 * The same check the skill tools run on `documentIds`. It accepts every uuid version, so a
 * document the save would act on (a channel import has a v5 id) is never left off the card.
 */
const DOCUMENT_ID_SCHEMA = z.uuid();

const useStyles = makeStyles()(theme => ({
  approvalBody: {
    ...getTypographyForSx(theme.typography.body2),
    color: theme.appColors.iconsType,
    fontSize: 14,
    marginTop: theme.spacing(1),
    whiteSpace: 'pre-wrap',
  },
  approvalFooter: {
    display: 'flex',
    flex: '1 1 auto',
    flexDirection: 'column',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  approvalFooterActions: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'flex-end',
  },
  approvalPlanCard: {
    '& ol, & ul': {
      marginBottom: theme.spacing(1),
      marginTop: theme.spacing(0.5),
      paddingLeft: theme.spacing(2.5),
    },
    '& p': {
      marginBottom: theme.spacing(0.75),
      marginTop: 0,
    },
    backgroundColor: theme.appColors.background,
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: theme.shape.borderRadius,
    color: theme.appColors.iconsType,
    marginTop: theme.spacing(1),
    padding: theme.spacing(1.25),
  },
  approvalPlanContent: {
    '& [data-streamdown="strong"]': {
      fontWeight: 700,
    },
    paddingRight: theme.spacing(0.5),
  },
  approvalPlanTitle: {
    ...getTypographyForSx(theme.typography.subtitle2),
    color: theme.appColors.iconsType,
    fontSize: 14,
    fontWeight: 700,
    marginBottom: theme.spacing(0.75),
  },
  checklist: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
  },
  checklistRow: {
    alignItems: 'center',
    color: theme.appColors.iconsType,
    cursor: 'pointer',
    display: 'flex',
    fontSize: 12,
    gap: theme.spacing(0.5),
  },
  formFields: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  inputRow: {
    '&:focus-within': {
      border: `1px solid ${theme.appColors.primary}`,
    },
    background: theme.palette.common.white,
    border: `1px solid #e2e8f0`,
    borderRadius: 6,
    minHeight: 32,
  },
  option: {
    '&.Mui-disabled': {
      opacity: 0.6,
    },
    '&:hover:not(.Mui-disabled)': {
      borderColor: theme.appColors.primary,
    },
    alignItems: 'center',
    background: '#ffffff',
    border: `1px solid #e2e8f0`,
    borderRadius: 6,
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'flex-start',
    minHeight: 32,
    padding: theme.spacing(0, 1.5),
    textAlign: 'left',
    width: '100%',
  },
  optionLabel: {
    ...getTypographyForSx(theme.typography.body2),
    color: theme.appColors.iconsType,
    fontSize: 12,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  optionNumber: {
    ...getTypographyForSx(theme.typography.body2),
    color: theme.appColors.iconsType,
    fontSize: 12,
  },
  optionSelected: {
    borderColor: theme.appColors.primary,
  },
  options: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  question: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  questionText: {
    ...getTypographyForSx(theme.typography.body2),
    color: theme.appColors.iconsType,
    fontSize: 12,
    fontWeight: 600,
  },
  requiredMark: {
    color: theme.palette.error.main,
  },
  skillDocumentList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  summary: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
  },
  summaryAnswer: {
    ...getTypographyForSx(theme.typography.body2),
    color: theme.appColors.iconsType,
    /**
     * Prefer keeping short answers intact beside the question; long answers
     * can still wrap without character-level breaking.
     */
    flexShrink: 0,
    fontSize: 12,
    fontWeight: 500,
    maxWidth: '45%',
    overflowWrap: 'break-word',
  },
  summaryIcon: {
    color: theme.appColors.secondaryType,
    flexShrink: 0,
  },
  summaryQuestion: {
    ...getTypographyForSx(theme.typography.body2),
    color: theme.appColors.iconsType,
    flex: '1 1 0',
    fontSize: 12,
    fontStyle: 'italic',
    minWidth: 0,
  },
  summaryRow: {
    alignItems: 'baseline',
    display: 'flex',
    gap: theme.spacing(0.75),
    minWidth: 0,
  },
  workflowApprovalAttachmentLinks: {
    alignItems: 'flex-start',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
  },
  workflowApprovalDetailLabel: {
    ...getTypographyForSx(theme.typography.caption),
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase',
  },
  workflowApprovalDetailRow: {
    alignItems: 'flex-start',
    borderTop: `1px solid ${theme.appColors.rule}`,
    display: 'grid',
    gap: theme.spacing(1),
    gridTemplateColumns: '120px 1fr',
    padding: theme.spacing(1, 0),
  },
  workflowApprovalDetailValue: {
    ...getTypographyForSx(theme.typography.body2),
    color: theme.appColors.iconsType,
    fontSize: 14,
    fontWeight: 600,
  },
  workflowApprovalDetails: {
    borderBottom: `1px solid ${theme.appColors.rule}`,
    marginTop: theme.spacing(1.25),
  },
  workflowApprovalForm: {
    display: 'flex',
    flexDirection: 'column',
  },
  workflowApprovalHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25),
    marginTop: theme.spacing(1),
  },
  workflowApprovalHelper: {
    ...getTypographyForSx(theme.typography.body2),
    color: theme.appColors.secondaryType,
    fontSize: 14,
    marginTop: theme.spacing(0.5),
  },
  workflowApprovalTitle: {
    ...getTypographyForSx(theme.typography.subtitle1),
    color: theme.appColors.iconsType,
    fontSize: 16,
    fontWeight: 700,
  },
  workflowApprovalUseWhen: {
    ...getTypographyForSx(theme.typography.body2),
    color: theme.appColors.iconsType,
    fontSize: 14,
  },
}));

/**
 * The interactive question card above chat input while an askUser turn is
 * pending. Every field renders in a vertically-scrollable list; up/down arrows
 * move focus between fields, and Submit sends every answer together. Untouched
 * fields submit as skipped. Options act as suggestions beside a free-text box.
 */
export function AskUserQuestionsCard({
  maxHeight,
  messageId,
  questions,
  title,
  toolCallId,
}: {
  messageId: string;
  questions: AskUserQuestion[];
  toolCallId: string;
  maxHeight?: number | null;
  /** Heading naming what the answers are for. Falls back to a generic one. */
  title?: string | null;
}) {
  const total = questions.length;
  const { classes } = useStyles();
  const { addToolOutput } = useChatAdapter();
  const { enqueueSnackbar } = useSnackbar();
  const [expanded, setExpanded] = React.useState(true);
  const [drafts, setDrafts] = React.useState<Record<number, AskUserDraft>>(() =>
    buildInitialDrafts(questions)
  );
  const [focusedIndex, setFocusedIndex] = React.useState(0);
  const [submitting, setSubmitting] = React.useState(false);
  const [uploading, setUploading] = React.useState<Record<number, boolean>>({});
  const fieldRefs = React.useRef<(HTMLDivElement | null)[]>([]);
  const formFieldsRef = React.useRef<HTMLDivElement | null>(null);
  const submitDisabledReason = getSubmitDisabledReason({
    drafts,
    questions,
    uploading,
  });
  const setDraft = (index: number, next: AskUserDraft) =>
    setDrafts(current => ({ ...current, [index]: next }));
  const clearDraft = (index: number) =>
    setDrafts(current => {
      const next = { ...current };
      delete next[index];
      return next;
    });
  const toggleDraftOption = (index: number, option: AskUserOption) =>
    setDrafts(current => ({
      ...current,
      [index]: toggleMultiOption(current[index], option),
    }));

  /**
   * Move focus to a field by scrolling ONLY the field container (not the page)
   * and focusing its control with `preventScroll`, so the header/arrows stay put
   * — `scrollIntoView` + a bare `focus()` would bubble and scroll the whole
   * panel, stranding the user with the header off-screen.
   */
  const focusField = (index: number) => {
    const clamped = Math.max(0, Math.min(total - 1, index));
    setFocusedIndex(clamped);
    const container = formFieldsRef.current;
    const field = fieldRefs.current[clamped];
    if (container && field) {
      const delta = field.getBoundingClientRect().top - container.getBoundingClientRect().top;
      container.scrollBy({ behavior: 'smooth', top: delta });
    }
    focusFieldControl(field);
  };

  const submitAnswers = async (answers: AskUserAnswer[]) => {
    setSubmitting(true);
    try {
      await addToolOutput({
        messageId,
        output: { answers },
        tool: 'askUser',
        toolCallId,
      });
    } catch (error) {
      Sentry.captureException(error);
      enqueueSnackbar('Failed to submit the form. Please try again.', {
        variant: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = () => void submitAnswers(buildIntakeFormAnswers(questions, drafts));

  const handleSkip = () =>
    void submitAnswers(
      questions.map(question => ({
        answer: ASK_USER_SKIPPED_ANSWER,
        question: question.question,
      }))
    );

  return (
    <PendingInteractionCardChrome
      bodyRef={formFieldsRef}
      expanded={expanded}
      footer={
        <>
          <CardSecondaryAction disabled={submitting} onClick={handleSkip}>
            Skip
          </CardSecondaryAction>
          <CardPrimaryAction
            disabled={submitting}
            disabledReason={submitDisabledReason}
            onClick={handleSubmit}
          >
            Submit
          </CardPrimaryAction>
        </>
      }
      headerActions={
        expanded &&
        total > 1 && <CardPager index={focusedIndex} total={total} onMove={focusField} />
      }
      maxHeight={maxHeight}
      title={title || 'Questions'}
      toggleLabel="questions"
      onToggleExpanded={() => setExpanded(current => !current)}
    >
      <div className={classes.formFields}>
        {questions.map((question, index) => (
          <div
            key={index}
            ref={element => {
              fieldRefs.current[index] = element;
            }}
            onFocusCapture={() => setFocusedIndex(index)}
          >
            <AskUserQuestionBlock
              draft={drafts[index]}
              question={question}
              submitting={submitting}
              onClearDraft={() => clearDraft(index)}
              onFilesChange={files => setDraft(index, { files, kind: 'file' })}
              onSelectOption={option => setDraft(index, { kind: 'option', option })}
              onTextChange={text => setDraft(index, { kind: 'text', text })}
              onToggleOption={option => toggleDraftOption(index, option)}
              onUploadingChange={isUploading =>
                setUploading(current => ({
                  ...current,
                  [index]: isUploading,
                }))
              }
            />
          </div>
        ))}
      </div>
    </PendingInteractionCardChrome>
  );
}

/** Strip anything that can't be part of a number as the user types. */
function AskUserQuestionBlock({
  draft,
  onClearDraft,
  onFilesChange,
  onSelectOption,
  onTextChange,
  onToggleOption,
  onUploadingChange,
  question,
  submitting,
}: {
  draft: AskUserDraft | undefined;
  onClearDraft: () => void;
  onFilesChange: (files: AskUserFile[]) => void;
  onSelectOption: (option: AskUserOption) => void;
  onTextChange: (text: string) => void;
  onToggleOption: (option: AskUserOption) => void;
  onUploadingChange: (uploading: boolean) => void;
  question: AskUserQuestion;
  submitting: boolean;
}) {
  const { classes } = useStyles();
  const options = question.options ?? null;
  const hasOptions = Boolean(options?.length);
  const label = (
    <Typography className={classes.questionText}>
      {question.question}
      {question.required ? <span className={classes.requiredMark}> *</span> : null}
    </Typography>
  );

  if (question.kind === 'file') {
    return (
      <div className={classes.question}>
        {label}
        <AskUserFileUpload
          disabled={submitting}
          files={draft?.kind === 'file' ? draft.files : []}
          maxFiles={question.maxFiles}
          onChange={onFilesChange}
          onUploadingChange={onUploadingChange}
        />
      </div>
    );
  }

  if (question.kind === 'date') {
    const dateText = draft?.kind === 'text' ? draft.text : '';
    return (
      <div className={classes.question}>
        {label}
        {options?.length ? (
          <AskUserOptionButtons
            disabled={submitting}
            isSelected={option => dateText === option.id}
            options={options}
            onPick={option => onTextChange(option.id)}
          />
        ) : null}
        <AskUserDateField disabled={submitting} value={dateText} onChange={onTextChange} />
      </div>
    );
  }

  if (question.kind === 'multiSelect' && options?.length) {
    const selectedIds = new Set(
      draft?.kind === 'multiOption'
        ? draft.options.map(option => `${option.source}:${option.id}`)
        : []
    );
    return (
      <div className={classes.question}>
        {label}
        <div className={classes.checklist}>
          {options.map(option => (
            <label key={`${option.source}:${option.id}`} className={classes.checklistRow}>
              <AlineCheckbox
                checked={selectedIds.has(`${option.source}:${option.id}`)}
                disabled={submitting}
                size="small"
                onChange={() => onToggleOption(option)}
              />
              {option.label}
            </label>
          ))}
        </div>
      </div>
    );
  }

  if (question.kind === 'user') {
    return (
      <div className={classes.question}>
        {label}
        <AskUserOrgMemberField
          disabled={submitting}
          maxSelections={1}
          selectedOptions={draft?.kind === 'option' ? [draft.option] : []}
          onChangeOptions={options => {
            const [option] = options;
            if (option) onSelectOption(option);
            else onClearDraft();
          }}
        />
      </div>
    );
  }

  return (
    <div className={classes.question}>
      {label}
      {question.kind === 'select' && options?.length ? (
        <AskUserOptionButtons
          disabled={submitting}
          isSelected={option =>
            draft?.kind === 'option' &&
            draft.option.id === option.id &&
            draft.option.source === option.source
          }
          options={options}
          onPick={onSelectOption}
        />
      ) : null}
      <AlineInput
        disabled={submitting}
        inputClassName={classes.inputRow}
        placeholder={
          question.placeholder ??
          (hasOptions ? 'Or add more information...' : 'Type your answer...')
        }
        value={draft?.kind === 'text' ? draft.text : ''}
        onChange={event => onTextChange(event.target.value)}
      />
    </div>
  );
}

/**
 * Single org-member picker for askUser `type: "user"`. Reuses the shared
 * UserAutocomplete (always multi under the hood) but keeps at most one selection
 * so the answer shape stays `selectedOption` with source "user".
 */
/**
 * Org-member picker. Multi-select by default, since a task can hold several
 * assignees; `maxSelections` caps it for the single-answer question card.
 * `maxChips` only limits how many chips render, so the cap lives in onChange.
 */
function AskUserOrgMemberField({
  disabled,
  maxSelections,
  onChangeOptions,
  selectedOptions,
}: {
  disabled: boolean;
  onChangeOptions: (options: AskUserOption[]) => void;
  selectedOptions: AskUserOption[];
  maxSelections?: number;
}) {
  const { orgMembers } = useGetOrgMembers({
    variables: { filter: Apollo.OrgMemberFilter.Active },
  });
  // Resolve from org members so avatarColor is preserved (draft only has id/label).
  const value: UserOption[] = selectedOptions.map(option => {
    const member = orgMembers.find(orgMember => orgMember.id === option.id);
    return {
      __typename: 'User',
      avatarColor: member?.avatarColor ?? '',
      displayName: member?.displayName ?? option.label,
      id: option.id,
    };
  });

  return (
    <UserAutocomplete
      openOnFocus
      disabled={disabled}
      maxChips={maxSelections}
      placeholder={maxSelections === 1 ? 'Select a user...' : 'Select users...'}
      value={value}
      onChange={users => {
        const next: AskUserOption[] = users.map(user => ({
          id: user.id,
          label: user.displayName,
          source: 'user',
        }));
        // Keep the newest picks when the control holds more than the cap allows.
        onChangeOptions(maxSelections === undefined ? next : next.slice(-maxSelections));
      }}
    />
  );
}

/**
 * The compact, read-only trace left in the chat stream once an askUser turn is
 * answered. Renders nothing while the turn is still pending — the interactive
 * card lives in the artifact area above the input until then.
 */
export function AskUserInlineSummary({ output }: { output: unknown }) {
  const { classes } = useStyles();
  const answers = getAskUserAnswers(output);
  if (!answers) return null;

  return (
    <div className={classes.summary}>
      {answers.map((entry, index) => (
        <div key={index} className={classes.summaryRow}>
          <Check className={classes.summaryIcon} size={12} />
          <Typography className={classes.summaryQuestion}>{entry.question}:</Typography>
          <Typography className={classes.summaryAnswer}>
            {entry.answer === ASK_USER_SKIPPED_ANSWER ? 'Not answered' : entry.answer}
          </Typography>
        </div>
      ))}
    </div>
  );
}

/**
 * The Approve/Skip card shown in the same artifact area as the question card,
 * so every assistant interaction shares one surface. Submits through the same
 * chat adapter the inline approval buttons used.
 */
type ApprovalCardProps = {
  approvalId: string;
  input: unknown;
  messageId: string;
  toolName: string;
  maxHeight?: number | null;
};

export function ApprovalCard({
  approvalId,
  input,
  maxHeight,
  messageId,
  toolName,
}: ApprovalCardProps) {
  const { classes } = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [expanded, setExpanded] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const { addToolApprovalResponse } = useChatAdapter();
  const assignTaskId = getAssignTaskId(toolName, input);
  const peopleUserIds = getReassignApprovalUserIds(toolName, input);
  const { data: assignTaskData, loading: assignTaskLoading } = Apollo.useGetTaskQuery({
    skip: !assignTaskId,
    variables: { taskId: assignTaskId ?? '' },
  });
  const {
    loading: peopleLoading,
    orgContacts,
    orgMembers,
  } = useGetOrgMembers({
    skip: peopleUserIds.length === 0,
    variables: { filter: Apollo.OrgMemberFilter.Active },
  });
  const task = assignTaskData?.task;
  const userNamesById = useMemo(() => {
    const names = new Map<string, string>();
    const setLabel = (userId: string, name: string, email: string) => {
      const label = formatApprovalPersonLabel({ email, name });
      if (label) names.set(userId, label);
    };
    for (const assignment of task?.assignments ?? []) {
      const user = getAssignmentUser(assignment);
      if (user) setLabel(user.id, user.name, user.email);
    }
    for (const contact of orgContacts) setLabel(contact.id, contact.displayName, contact.email);
    for (const member of orgMembers) setLabel(member.id, member.displayName, member.email);
    return names;
  }, [orgContacts, orgMembers, task?.assignments]);
  const taskTitle = task?.title ?? null;
  const peopleNamesReady = peopleUserIds.length === 0 || !peopleLoading;
  const { body, title } = getApprovalCopy(toolName, input, {
    taskTitle,
    userNamesById: peopleNamesReady ? userNamesById : undefined,
  });
  const skillProposal = useMemo(
    () => (getIsSkillProposalToolName(toolName) ? getSkillProposalApprovalDetails(input) : null),
    [input, toolName]
  );

  const respond = async (approved: boolean) => {
    try {
      setSubmitting(true);
      await addToolApprovalResponse({ approved, id: approvalId, messageId });
    } catch (error) {
      Sentry.captureException(error);
      enqueueSnackbar('Failed to submit your response. Please try again.', {
        variant: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PendingInteractionCardChrome
      expanded={expanded}
      footer={
        <div className={classes.approvalFooter}>
          <div className={classes.approvalFooterActions}>
            <CardSecondaryAction disabled={submitting} onClick={() => void respond(false)}>
              Skip
            </CardSecondaryAction>
            <CardPrimaryAction
              disabled={
                submitting ||
                (Boolean(assignTaskId) && assignTaskLoading && !taskTitle) ||
                !peopleNamesReady
              }
              onClick={() => void respond(true)}
            >
              Approve
            </CardPrimaryAction>
          </div>
        </div>
      }
      maxHeight={maxHeight}
      title={title}
      toggleLabel="approval"
      onToggleExpanded={() => setExpanded(current => !current)}
    >
      <ApprovalCardBody
        body={body}
        input={input}
        skillProposal={skillProposal}
        toolName={toolName}
      />
    </PendingInteractionCardChrome>
  );
}

/** The documents a proposed skill would attach, as chips that open each one. */
const SkillProposalDocuments = React.memo(function SkillProposalDocuments({
  documentIds,
  title,
}: {
  documentIds: UUID[];
  title: string;
}) {
  const { classes } = useStyles();
  const mentionEntityIds = useMemo(() => ({ documentIds }), [documentIds]);
  const { accessResultMap } = useMentionEntitiesQuery(mentionEntityIds);

  return (
    <div className={classes.approvalPlanCard}>
      <Typography className={classes.approvalPlanTitle}>{title}</Typography>
      <ul aria-label={title} className={classes.skillDocumentList}>
        {documentIds.map(documentId => (
          <li key={documentId}>
            <WorkflowApprovalDocumentChip
              accessResult={accessResultMap.get(documentId) ?? null}
              documentId={documentId}
            />
          </li>
        ))}
      </ul>
    </div>
  );
});

/**
 * The skill the user decides on before anything is saved: its name, the description agents
 * use to pick it, the SKILL.md instructions, and the copies of documents it will keep. For a
 * change, the summary of what changes comes first. Read-only on purpose: asking for
 * something different in the chat supersedes this proposal and gets a new one.
 */
const SkillProposalApprovalBody = React.memo(function SkillProposalApprovalBody({
  details,
  fallbackBody,
  isUpdate,
}: {
  details: SkillProposalApprovalDetails | null;
  fallbackBody: string;
  isUpdate: boolean;
}) {
  const { classes } = useStyles();
  if (!details) return <Typography className={classes.approvalBody}>{fallbackBody}</Typography>;

  return (
    <div className={classes.workflowApprovalForm}>
      <div className={classes.workflowApprovalHeader}>
        <Typography className={classes.workflowApprovalTitle}>{details.name}</Typography>
        <Typography className={classes.workflowApprovalHelper}>
          {isUpdate
            ? "Approving saves these changes to the skill's draft. Test them from the skill page, then publish to make them live."
            : 'Approving creates an unpublished skill. Test it from the skill page, then publish it.'}
        </Typography>
      </div>
      <div className={classes.workflowApprovalDetails}>
        {isUpdate && details.changeSummary && (
          <WorkflowApprovalDetailRow label="Changes" value={details.changeSummary} />
        )}
        <WorkflowApprovalDetailRow
          label="Use when"
          value={details.description}
          valueClassName={classes.workflowApprovalUseWhen}
        />
      </div>
      {details.documentIds.length > 0 && (
        <SkillProposalDocuments
          documentIds={details.documentIds}
          title={isUpdate ? 'Documents to add' : 'Documents to attach'}
        />
      )}
      <div className={classes.approvalPlanCard}>
        <Typography className={classes.approvalPlanTitle}>SKILL.md</Typography>
        <div className={classes.approvalPlanContent}>
          <AlineMarkdown markdownString={details.instructions} type="message" />
        </div>
      </div>
    </div>
  );
});

function ApprovalCardBody({
  body,
  input,
  skillProposal,
  toolName,
}: {
  body: string;
  input: unknown;
  skillProposal: SkillProposalApprovalDetails | null;
  toolName: string;
}) {
  const { classes } = useStyles();

  if (toolName === ALINE_TOOL_NAMES.workflowCreateDraft)
    return <WorkflowCreateDraftApprovalBody fallbackBody={body} input={input} />;

  if (toolName === ALINE_TOOL_NAMES.generatePlaybookTool)
    return <PlaybookGenerateApprovalBody fallbackBody={body} input={input} />;

  if (getIsSkillProposalToolName(toolName))
    return (
      <SkillProposalApprovalBody
        details={skillProposal}
        fallbackBody={body}
        isUpdate={toolName === ALINE_TOOL_NAMES.updateSkillTool}
      />
    );

  return <Typography className={classes.approvalBody}>{body}</Typography>;
}

/**
 * The plan the user decides on before any playbook is written: the documents
 * it was read from, the party it represents, and the categories and rules it
 * will cover. Read-only on purpose — changing the plan means saying so in the
 * chat, which supersedes this turn and proposes a new one.
 */
function PlaybookGenerateApprovalBody({
  fallbackBody,
  input,
}: {
  fallbackBody: string;
  input: unknown;
}) {
  const { classes } = useStyles();
  const details = useMemo(() => getPlaybookGenerateApprovalDetails(input), [input]);
  const mentionEntityIds = useMemo(() => ({ documentIds: details?.documentIds ?? [] }), [details]);
  const { accessResultMap } = useMentionEntitiesQuery(mentionEntityIds);
  if (!details?.generationPlan)
    return <Typography className={classes.approvalBody}>{fallbackBody}</Typography>;

  return (
    <div className={classes.workflowApprovalForm}>
      <div className={classes.workflowApprovalHeader}>
        <Typography className={classes.workflowApprovalTitle}>
          {details.title ?? 'Proposed plan'}
        </Typography>
        <Typography className={classes.workflowApprovalHelper}>
          Review what the AI will build before it writes anything. Approving this plan does not
          change any playbook yet.
        </Typography>
      </div>
      <div className={classes.workflowApprovalDetails}>
        {details.documentIds.length > 0 && (
          <WorkflowApprovalDetailRow
            label={details.documentIds.length === 1 ? 'Source' : 'Sources'}
            value={
              <div className={classes.workflowApprovalAttachmentLinks}>
                {details.documentIds.map(documentId => (
                  <WorkflowApprovalDocumentChip
                    key={documentId}
                    accessResult={accessResultMap.get(documentId) ?? null}
                    documentId={documentId}
                  />
                ))}
              </div>
            }
          />
        )}
        <WorkflowApprovalDetailRow label="Represents" value={details.party} />
      </div>
      <div className={classes.approvalPlanCard}>
        <Typography className={classes.approvalPlanTitle}>Playbook generation plan</Typography>
        <div className={classes.approvalPlanContent}>
          <AlineMarkdown markdownString={details.generationPlan} type="message" />
        </div>
      </div>
    </div>
  );
}

function WorkflowCreateDraftApprovalBody({
  fallbackBody,
  input,
}: {
  fallbackBody: string;
  input: unknown;
}) {
  const { classes } = useStyles();
  const details = useMemo(() => getCreateDraftApprovalDetails(input), [input]);
  const mentionEntityIds = useMemo(
    () => ({
      documentIds: details?.attachments.map(attachment => attachment.documentId) ?? [],
    }),
    [details]
  );
  const { accessResultMap } = useMentionEntitiesQuery(mentionEntityIds);
  if (!details?.buildPlan)
    return <Typography className={classes.approvalBody}>{fallbackBody}</Typography>;

  return (
    <div className={classes.workflowApprovalForm}>
      <div className={classes.workflowApprovalHeader}>
        <Typography className={classes.workflowApprovalTitle}>{details.name}</Typography>
        <Typography className={classes.workflowApprovalHelper}>
          Aline AI drafted a plan for a new workflow. Approve it to create the draft and start
          building the workflow phases, or skip to reject this plan.
        </Typography>
      </div>
      <div className={classes.workflowApprovalDetails}>
        <WorkflowApprovalDetailRow label="Workflow" value={details.name} />
        {details.attachments.length > 0 && (
          <WorkflowApprovalDetailRow
            label="Attachments"
            value={
              <div className={classes.workflowApprovalAttachmentLinks}>
                {details.attachments.map(attachment => (
                  <WorkflowApprovalDocumentChip
                    key={attachment.documentId}
                    accessResult={accessResultMap.get(attachment.documentId) ?? null}
                    documentId={attachment.documentId}
                  />
                ))}
              </div>
            }
          />
        )}
      </div>
      <div className={classes.approvalPlanCard}>
        <Typography className={classes.approvalPlanTitle}>Proposed workflow plan</Typography>
        <div className={classes.approvalPlanContent}>
          <AlineMarkdown markdownString={details.buildPlan} type="message" />
        </div>
      </div>
    </div>
  );
}

function WorkflowApprovalDocumentChip({
  accessResult,
  documentId,
}: {
  accessResult: DocumentMentionAccessResult | null;
  documentId: string;
}) {
  const { document, errorReason, isProcessing, loading, ocrStatus } = React.useMemo(
    () => computeDocumentAccessData(accessResult),
    [accessResult]
  );
  const displayText = document?.name || 'Untitled Document';
  const handleOpen = React.useCallback(
    (event: React.MouseEvent<HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();
      if (!document) return;

      openLink(link.DocumentDetail({ documentId }), { title: displayText });
    },
    [displayText, document, documentId]
  );

  return (
    <DocumentMentionChip
      readOnly
      accessRequested={false}
      displayText={displayText}
      document={document}
      errorReason={errorReason}
      isProcessing={isProcessing}
      loading={loading}
      ocrStatus={ocrStatus}
      onOpen={handleOpen}
    />
  );
}

function WorkflowApprovalDetailRow({
  label,
  value,
  valueClassName,
}: {
  label: string;
  value: React.ReactNode;
  valueClassName?: string;
}) {
  const { classes } = useStyles();
  return (
    <div className={classes.workflowApprovalDetailRow}>
      <Typography className={classes.workflowApprovalDetailLabel}>{label}</Typography>
      <Typography className={valueClassName ?? classes.workflowApprovalDetailValue} component="div">
        {value}
      </Typography>
    </div>
  );
}

/**
 * Tools with specialized approval-card copy/UI. Values come from
 * `ALINE_TOOL_NAMES` so they cannot drift from `@aline/common` / server keys.
 * Persisted uiParts still carry `toolName` as a plain string, so callers narrow
 * with {@link getIsApprovalToolName}.
 */
export const APPROVAL_TOOL_NAMES = {
  createReport: ALINE_TOOL_NAMES.createReportTool,
  createSkill: ALINE_TOOL_NAMES.createSkillTool,
  generatePlaybook: ALINE_TOOL_NAMES.generatePlaybookTool,
  reassignTask: ALINE_TOOL_NAMES.reassignTaskTool,
  updateSkill: ALINE_TOOL_NAMES.updateSkillTool,
  workflowCreateDraft: ALINE_TOOL_NAMES.workflowCreateDraft,
  workflowLaunchTemplate: ALINE_TOOL_NAMES.workflowLaunchTemplate,
} as const satisfies Record<string, AlineToolName>;

export type ApprovalToolName = (typeof APPROVAL_TOOL_NAMES)[keyof typeof APPROVAL_TOOL_NAMES];

type ApprovalCopy = { body: string; title: string };

type ApprovalCopyOptions = {
  taskTitle?: string | null;
  userNamesById?: ReadonlyMap<string, string>;
};

const DEFAULT_APPROVAL_COPY = {
  body: 'Aline is ready to take an action that needs your approval.',
  title: 'Approval needed',
} satisfies ApprovalCopy;

const APPROVAL_COPY_BY_TOOL = {
  [APPROVAL_TOOL_NAMES.createReport]: (
    input: unknown,
    _options: ApprovalCopyOptions
  ): ApprovalCopy => {
    const name = getInputString(input, 'name');
    return {
      body: name
        ? `Aline wants to create the report "${name}".`
        : 'Aline wants to create a report.',
      title: 'AI wants to create a report',
    };
  },
  [APPROVAL_TOOL_NAMES.createSkill]: (
    input: unknown,
    _options: ApprovalCopyOptions
  ): ApprovalCopy => {
    const name = getInputString(input, 'name');
    return {
      body: name ? `Aline wants to create the skill "${name}".` : 'Aline wants to create a skill.',
      title: 'AI wants to create a skill',
    };
  },
  [APPROVAL_TOOL_NAMES.updateSkill]: (
    input: unknown,
    _options: ApprovalCopyOptions
  ): ApprovalCopy => {
    const name = getInputString(input, 'name');
    return {
      body: name ? `Aline wants to update the skill "${name}".` : 'Aline wants to update a skill.',
      title: 'AI wants to update a skill',
    };
  },
  [APPROVAL_TOOL_NAMES.generatePlaybook]: (
    input: unknown,
    _options: ApprovalCopyOptions
  ): ApprovalCopy => {
    const title = getInputString(input, 'title');
    return {
      body: title
        ? `Aline wants to build "${title}".`
        : 'Aline wants to build a playbook from this document.',
      title: 'AI wants to create a playbook',
    };
  },
  [APPROVAL_TOOL_NAMES.reassignTask]: (
    input: unknown,
    options: ApprovalCopyOptions
  ): ApprovalCopy => ({
    body: describeReassignTaskApproval(input, options),
    title: 'Change task assignments',
  }),
  [APPROVAL_TOOL_NAMES.workflowCreateDraft]: (
    input: unknown,
    _options: ApprovalCopyOptions
  ): ApprovalCopy => {
    const summary = summarizeCreateDraftApproval(input);
    return {
      body: summary
        ? `Aline wants to create this draft workflow: ${summary}`
        : 'Aline wants to create a draft workflow.',
      title: 'Create draft workflow',
    };
  },
  [APPROVAL_TOOL_NAMES.workflowLaunchTemplate]: (
    input: unknown,
    _options: ApprovalCopyOptions
  ): ApprovalCopy => {
    const name = getInputString(input, 'name');
    return {
      body: name
        ? `Aline wants to launch the selected template and title your workflow "${name}".`
        : 'Aline wants to launch a workflow from the selected template.',
      title: 'Approve launch',
    };
  },
} satisfies Record<
  ApprovalToolName,
  (input: unknown, options: ApprovalCopyOptions) => ApprovalCopy
>;

export function getIsApprovalToolName(toolName: string): toolName is ApprovalToolName {
  return Object.values(APPROVAL_TOOL_NAMES).some(name => name === toolName);
}

export function getApprovalCopy(
  toolName: string,
  input: unknown,
  options: ApprovalCopyOptions = {}
): ApprovalCopy {
  if (!getIsApprovalToolName(toolName)) return DEFAULT_APPROVAL_COPY;
  return APPROVAL_COPY_BY_TOOL[toolName](input, options);
}

/** "Name (email)" for a person with a name, or just the email when the name is missing or is the email. */
export function formatApprovalPersonLabel({ email, name }: { email: string; name: string }) {
  const trimmedName = name.trim();
  const trimmedEmail = email.trim();
  if (!trimmedEmail) return trimmedName;
  if (!trimmedName || trimmedName === trimmedEmail) return trimmedEmail;
  return `${trimmedName} (${trimmedEmail})`;
}

/** Task id from a reassignTask approval input, when present and valid. */
export function getAssignTaskId(toolName: string, input: unknown): UUID | null {
  if (toolName !== APPROVAL_TOOL_NAMES.reassignTask) return null;
  const taskId = getInputString(input, 'taskId');
  if (!taskId || !isUUIDV4(taskId)) return null;
  return taskId;
}

function getReassignApprovalUserIds(toolName: string, input: unknown): string[] {
  if (toolName !== APPROVAL_TOOL_NAMES.reassignTask) return [];
  const change = getReassignChange(input);
  if (!change) return [];
  return [...new Set([...change.add.map(person => person.userId), ...change.remove])];
}

type ReassignPerson = { isRequired: boolean; role: 'assignee' | 'reviewer'; userId: string };

type ReassignChange = { add: ReassignPerson[]; delegateToAlineAi: boolean; remove: string[] };

// The people a reassignTask call adds and removes. Null when any entry is malformed.
function getReassignChange(input: unknown): ReassignChange | null {
  if (!getIsPlainRecord(input)) return null;
  const rawAdd = Array.isArray(input.add) ? input.add : [];
  const rawRemove = Array.isArray(input.remove) ? input.remove : [];

  const add: ReassignPerson[] = [];
  for (const person of rawAdd) {
    if (!getIsPlainRecord(person) || !getIsUserId(person.userId)) return null;
    if (person.role !== 'assignee' && person.role !== 'reviewer') return null;
    add.push({ isRequired: person.isRequired === true, role: person.role, userId: person.userId });
  }
  const remove = rawRemove.filter(getIsUserId);
  if (remove.length !== rawRemove.length) return null;

  return { add, delegateToAlineAi: input.delegateToAlineAi === true, remove };
}

function getIsUserId(value: unknown): value is string {
  return typeof value === 'string' && isUUIDV4(value);
}

function describeReassignTaskApproval(input: unknown, options: ApprovalCopyOptions) {
  const taskLabel = options.taskTitle ? `"${options.taskTitle}"` : 'this task';
  const fallback = `Aline wants to change the people on ${taskLabel}.`;
  const change = getReassignChange(input);
  const { userNamesById } = options;
  if (!change || !userNamesById) return fallback;

  const toUserIds = (people: readonly ReassignPerson[]) => people.map(person => person.userId);
  const reviewers = change.add.filter(person => person.role === 'reviewer');
  const changes = [
    ...describeAdded(
      toUserIds(change.add.filter(person => person.role === 'assignee')),
      'assignee',
      userNamesById
    ),
    ...describeAdded(
      toUserIds(reviewers.filter(person => !person.isRequired)),
      'reviewer',
      userNamesById
    ),
    ...describeAdded(
      toUserIds(reviewers.filter(person => person.isRequired)),
      'requiredReviewer',
      userNamesById
    ),
  ];
  if (change.remove.length > 0)
    changes.push(`remove ${formatPeopleList(change.remove, userNamesById)}`);
  if (change.delegateToAlineAi) changes.push('hand the work to Aline AI');

  if (changes.length === 0) return fallback;
  return `On ${taskLabel}, Aline wants to ${formatNameList(changes)}.`;
}

const ROLE_NOUNS = {
  assignee: { many: 'assignees', one: 'an assignee' },
  requiredReviewer: { many: 'required reviewers', one: 'a required reviewer' },
  reviewer: { many: 'reviewers', one: 'a reviewer' },
} as const satisfies Record<string, { many: string; one: string }>;

type RoleNoun = keyof typeof ROLE_NOUNS;

function describeAdded(
  userIds: readonly string[],
  role: RoleNoun,
  userNamesById: ReadonlyMap<string, string>
): string[] {
  if (userIds.length === 0) return [];
  return [`add ${formatPeopleList(userIds, userNamesById)} as ${getRoleNoun(role, userIds)}`];
}

function getRoleNoun(role: RoleNoun, userIds: readonly string[]) {
  return userIds.length === 1 ? ROLE_NOUNS[role].one : ROLE_NOUNS[role].many;
}

function formatPeopleList(
  userIds: readonly string[],
  userNamesById: ReadonlyMap<string, string>
): string {
  let unresolvedCount = 0;
  const names: string[] = [];
  for (const userId of userIds) {
    const name = userNamesById.get(userId)?.trim();
    if (name) names.push(name);
    else unresolvedCount += 1;
  }

  if (unresolvedCount === 1) names.push('1 other person');
  else if (unresolvedCount > 1) names.push(`${unresolvedCount} other people`);

  return formatNameList(names);
}

function formatNameList(names: readonly string[]): string {
  const last = names.at(-1);
  if (typeof last === 'undefined') return '';
  if (names.length === 1) return last;

  const leading = names.slice(0, -1).join(', ');
  if (names.length === 2) return `${leading} and ${last}`;
  return `${leading}, and ${last}`;
}

/** Mirror the stream-side create-draft summary for the above-input approval card. */
export function summarizeCreateDraftApproval(input: unknown) {
  const details = getCreateDraftApprovalDetails(input);
  if (!details) return null;

  if (details.buildPlan)
    return `"${details.name}" (${details.attachmentCount} attachment${details.attachmentCount === 1 ? '' : 's'}).\n\n${details.buildPlan}`;

  return `"${details.name}" (${details.attachmentCount} attachment${details.attachmentCount === 1 ? '' : 's'}, ${details.phaseCount} phase${details.phaseCount === 1 ? '' : 's'}, and ${details.taskCount} task${details.taskCount === 1 ? '' : 's'}).`;
}

export function getCreateDraftApprovalDetails(input: unknown) {
  if (!getIsPlainRecord(input)) return null;
  const name = getInputString(input, 'name');
  if (!name) return null;

  const phases = Array.isArray(input.phases) ? input.phases : [];
  const documentAttachments = Array.isArray(input.documentAttachments)
    ? input.documentAttachments
    : [];
  const documentIds = Array.isArray(input.documentIds) ? input.documentIds : [];
  const attachmentIds = [
    ...documentAttachments.flatMap(attachment => {
      if (!getIsPlainRecord(attachment)) return [];
      return typeof attachment.documentId === 'string' && isUUIDV4(attachment.documentId)
        ? [attachment.documentId]
        : [];
    }),
    ...documentIds.flatMap(documentId =>
      typeof documentId === 'string' && isUUIDV4(documentId) ? [documentId] : []
    ),
  ];
  const attachments = [...new Set(attachmentIds)].map(documentId => ({ documentId }));
  const taskCount = phases.reduce((total, phase) => {
    if (!getIsPlainRecord(phase) || !Array.isArray(phase.tasks)) return total;
    return total + phase.tasks.length;
  }, 0);

  return {
    attachmentCount: attachments.length,
    attachments,
    buildPlan: getInputString(input, 'buildPlan'),
    name,
    phaseCount: phases.length,
    taskCount,
  };
}

/** The approved-plan fields a playbook generation approval card renders. */
export function getPlaybookGenerateApprovalDetails(input: unknown) {
  if (!getIsPlainRecord(input)) return null;
  const party = getInputString(input, 'party');
  if (!party) return null;

  const documentIds = Array.isArray(input.documentIds) ? input.documentIds : [];
  return {
    documentIds: documentIds.flatMap(documentId =>
      typeof documentId === 'string' && isUUIDV4(documentId) ? [documentId] : []
    ),
    generationPlan: getInputString(input, 'generationPlan'),
    party,
    title: getInputString(input, 'title'),
  };
}

// Read a skill proposal's approval input. The documents are `documentIds`, the list the save attaches.
export function getSkillProposalApprovalDetails(
  input: unknown
): SkillProposalApprovalDetails | null {
  const name = getInputString(input, 'name');
  const description = getInputString(input, 'description');
  const instructions = getInputString(input, 'instructions');
  if (!getIsPlainRecord(input) || !name || !description || !instructions) return null;

  return {
    changeSummary: getInputString(input, 'changeSummary'),
    description,
    documentIds: getProposedDocumentIds(input.documentIds),
    instructions,
    name,
  };
}

function getIsSkillProposalToolName(toolName: string) {
  return (
    toolName === ALINE_TOOL_NAMES.createSkillTool || toolName === ALINE_TOOL_NAMES.updateSkillTool
  );
}

/** The distinct document ids a proposal lists, in order, checked the way the server does. */
function getProposedDocumentIds(documentIds: unknown) {
  if (!Array.isArray(documentIds)) return [];
  return [
    ...new Set(
      documentIds.filter(
        (documentId): documentId is string => DOCUMENT_ID_SCHEMA.safeParse(documentId).success
      )
    ),
  ];
}

function getInputString(input: unknown, key: string) {
  if (!getIsPlainRecord(input)) return null;
  const value = input[key];
  return typeof value === 'string' && value.trim() ? value : null;
}

const ASK_USER_KIND_BY_TYPE: Record<string, AskUserQuestionKind> = {
  date: 'date',
  email: 'email',
  file: 'file',
  multiSelect: 'multiSelect',
  multiline: 'multiline',
  number: 'number',
  select: 'select',
  text: 'text',
  user: 'user',
};

/**
 * Soft-parse the questions array from an askUser tool-call input. Current rows
 * are a discriminated union tagged by `type`. Older persisted rows used optional
 * booleans and carry no `type` — those map onto the same `kind` discriminant so
 * the card only ever branches on one field.
 */
export function parseAskUserQuestions(questions: unknown): AskUserQuestion[] | null {
  if (!Array.isArray(questions)) return null;

  const parsedQuestions = questions.flatMap(question => {
    if (!getIsPlainRecord(question)) return [];
    if (typeof question.question !== 'string' || !question.question.trim()) return [];

    const options = parseAskUserOptions(question.options);
    return [
      {
        kind: getAskUserQuestionKind(question, options),
        maxFiles:
          typeof question.maxFiles === 'number' &&
          Number.isInteger(question.maxFiles) &&
          question.maxFiles > 0
            ? question.maxFiles
            : null,
        options,
        placeholder: typeof question.placeholder === 'string' ? question.placeholder : null,
        question: question.question,
        required: question.required === true,
        /*
         * A single-select carries `suggestedOptionId`; multi-select and legacy
         * rows carry `suggestedOptionIds`. Normalize both to the array the card
         * reads.
         */
        suggestedOptionIds:
          parseStringArray(question.suggestedOptionIds) ??
          (typeof question.suggestedOptionId === 'string' ? [question.suggestedOptionId] : null),
        suggestedValue:
          typeof question.suggestedValue === 'string' ? question.suggestedValue : null,
      },
    ];
  });

  return parsedQuestions.length ? parsedQuestions : null;
}

/**
 * Prefer the schema `type` discriminant. Legacy boolean flags apply only when
 * `type` is absent, in the same precedence the old boolean UI used. A select /
 * multiSelect without parseable options degrades to free text.
 */
function getAskUserQuestionKind(
  question: Record<string, unknown>,
  options: AskUserOption[] | null
): AskUserQuestionKind {
  const type = typeof question.type === 'string' ? question.type : null;
  if (type !== null) {
    const kind = ASK_USER_KIND_BY_TYPE[type] ?? 'text';
    if ((kind === 'select' || kind === 'multiSelect') && !options?.length) return 'text';
    return kind;
  }

  if (question.expectsFile === true) return 'file';
  if (question.date === true) return 'date';
  if (question.multiSelect === true) return options?.length ? 'multiSelect' : 'text';
  if (question.multiline === true) return 'multiline';
  if (question.number === true) return 'number';
  if (question.email === true) return 'email';
  if (options?.length) return 'select';
  return 'text';
}

export function getAskUserAnswers(output: unknown): AskUserAnswer[] | null {
  if (!getIsPlainRecord(output)) return null;
  if (!Array.isArray(output.answers)) return null;

  const answers = output.answers.flatMap(entry => {
    if (!getIsPlainRecord(entry)) return [];
    const { answer, question } = entry;
    if (typeof question !== 'string' || typeof answer !== 'string' || !answer.trim()) return [];
    const files = parseAskUserFiles(entry.files);
    return [files ? { answer, files, question } : { answer, question }];
  });

  return answers.length ? answers : null;
}

function parseAskUserFiles(files: unknown): AskUserFile[] | null {
  if (!Array.isArray(files)) return null;

  const parsed = files.flatMap(file => {
    if (!getIsPlainRecord(file)) return [];
    const { documentId, name } = file;
    if (typeof documentId !== 'string' || typeof name !== 'string') return [];
    return [{ documentId, name }];
  });

  return parsed.length ? parsed : null;
}

/**
 * Collects one answer per question, returning null until every question is
 * resolved. A picked option or non-empty typed text answers a question;
 * skipping records the skipped sentinel. The null gate doubles as the
 * submit-disabled signal.
 */
function buildAskUserAnswers(
  questions: AskUserQuestion[],
  drafts: Record<number, AskUserDraft | undefined>
): AskUserAnswer[] | null {
  const answers: AskUserAnswer[] = [];

  for (const [index, question] of questions.entries()) {
    const draft = drafts[index];

    if (draft?.kind === 'skipped') {
      answers.push({
        answer: ASK_USER_SKIPPED_ANSWER,
        question: question.question,
      });
      continue;
    }

    if (draft?.kind === 'option') {
      answers.push({
        answer: draft.option.label,
        question: question.question,
        selectedOption: draft.option,
      });
      continue;
    }

    if (draft?.kind === 'multiOption') {
      if (draft.options.length === 0) return null;
      answers.push({
        answer: draft.options.map(option => option.label).join(', '),
        question: question.question,
        selectedOptions: draft.options,
      });
      continue;
    }

    if (draft?.kind === 'file') {
      if (draft.files.length === 0) return null;
      answers.push({
        answer: draft.files.map(file => file.name).join(', '),
        files: draft.files,
        question: question.question,
      });
      continue;
    }

    const text = draft?.kind === 'text' ? draft.text.trim() : '';
    if (!text) return null;
    answers.push({ answer: text, question: question.question });
  }

  return answers;
}

/**
 * One answer per intake field for a whole-form submit. Reuses the per-draft
 * mapping (option / multi-option / file / typed) via buildAskUserAnswers, and
 * defaults any unresolved field to the skipped sentinel so Submit always yields
 * exactly one answer per question.
 */
export function buildIntakeFormAnswers(
  questions: AskUserQuestion[],
  drafts: Record<number, AskUserDraft | undefined>
): AskUserAnswer[] {
  return questions.map((question, index) => {
    const [answer] = buildAskUserAnswers([question], { 0: drafts[index] }) ?? [];
    return answer ?? { answer: ASK_USER_SKIPPED_ANSWER, question: question.question };
  });
}

/**
 * Submit unlocks once every required field has an answer and no upload is still
 * in flight. Optional fields may stay blank; they submit as skipped.
 */
function getSubmitDisabledReason({
  drafts,
  questions,
  uploading,
}: {
  drafts: Record<number, AskUserDraft | undefined>;
  questions: AskUserQuestion[];
  uploading: Record<number, boolean>;
}): string {
  if (Object.values(uploading).some(Boolean)) return 'Wait for file uploads to finish';

  const missingRequiredQuestion = questions.find(
    (question, index) => question.required && !getIsDraftAnswered(drafts[index])
  );
  if (missingRequiredQuestion) return 'Answer required questions before submitting';

  return '';
}

/**
 * Seed each field's draft from the AI's suggested value so the intake form
 * renders pre-populated; the user edits what's off or submits as-is. A file
 * field can't be pre-suggested, and an option suggestion only seeds when it
 * matches a real option.
 */
export function buildInitialDrafts(questions: AskUserQuestion[]): Record<number, AskUserDraft> {
  const drafts: Record<number, AskUserDraft> = {};
  questions.forEach((question, index) => {
    const draft = buildSuggestedDraft(question);
    if (draft) drafts[index] = draft;
  });
  return drafts;
}

function buildSuggestedDraft(question: AskUserQuestion): AskUserDraft | null {
  if (question.kind === 'file' || question.kind === 'user') return null;
  const options = question.options ?? [];
  const picked = options.filter(option => (question.suggestedOptionIds ?? []).includes(option.id));

  /**
   * Seed the draft KIND that matches how the field renders (see
   * AskUserQuestionBlock), otherwise the pre-fill is invisible or submits the
   * wrong shape. A date renders a picker backed by a text draft, and a quick-pick
   * option id is itself an ISO date, so seed text either way (value wins).
   */
  if (question.kind === 'date') {
    const value = question.suggestedValue ?? picked[0]?.id ?? null;
    /**
     * Only seed a date the picker can actually show — an unparseable suggestion
     * would leave a blank picker but a hidden text draft that submits as-is.
     */
    return value && isValid(parseISO(value)) ? { kind: 'text', text: value } : null;
  }
  // A checklist renders a multiOption draft — seed only from matching options.
  if (question.kind === 'multiSelect') {
    return picked.length ? { kind: 'multiOption', options: picked } : null;
  }
  // A single-select seeds a matching option; free-text kinds seed suggestedValue.
  if (question.kind === 'select') {
    return picked.length ? { kind: 'option', option: picked[0] } : null;
  }
  return question.suggestedValue ? { kind: 'text', text: question.suggestedValue } : null;
}

function parseStringArray(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null;
  const strings = value.filter((item): item is string => typeof item === 'string');
  return strings.length ? strings : null;
}

function parseAskUserOptions(options: unknown): AskUserOption[] | null {
  if (!Array.isArray(options)) return null;

  const parsedOptions = options.flatMap(option => {
    if (!getIsPlainRecord(option)) return [];
    const { id, label, source } = option;
    if (typeof id !== 'string' || typeof label !== 'string' || typeof source !== 'string') {
      return [];
    }

    return [{ id, label, source }];
  });

  return parsedOptions.length ? parsedOptions : null;
}

/**
 * The pickable option buttons shared by the single-select question (pick one)
 * and the date question (ISO quick-picks alongside the picker). Each caller
 * supplies how an option maps to "selected" and what a pick does.
 */
function AskUserOptionButtons({
  disabled,
  isSelected,
  onPick,
  options,
}: {
  disabled: boolean;
  isSelected: (option: AskUserOption) => boolean;
  onPick: (option: AskUserOption) => void;
  options: AskUserOption[];
}) {
  const { classes } = useStyles();
  return (
    <div className={classes.options}>
      {options.map((option, optionIndex) => (
        <ButtonBase
          key={`${option.source}:${option.id}`}
          className={clsx(classes.option, isSelected(option) && classes.optionSelected)}
          disabled={disabled}
          onClick={() => onPick(option)}
        >
          <Typography className={classes.optionNumber}>{optionIndex + 1}</Typography>
          <Typography className={classes.optionLabel}>{option.label}</Typography>
        </ButtonBase>
      ))}
    </div>
  );
}

/**
 * Date control for an askUser date question, backed by the shared
 * AlineDatePicker. Values round-trip as `YYYY-MM-DD` strings (the intake date
 * shape), matching how the intake form's own date field stores them.
 */
function AskUserDateField({
  disabled,
  onChange,
  value,
}: {
  onChange: (value: string) => void;
  value: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const parsed = value ? parseISO(value) : null;
  return (
    <AlineDatePicker
      dateFieldProps={{ fullWidth: true }}
      disabled={disabled}
      enableFlip={true}
      open={open}
      placement="bottom-end"
      setOpen={setOpen}
      value={parsed && isValid(parsed) ? parsed : null}
      onDateChange={date => onChange(date && isValid(date) ? format(date, 'yyyy-MM-dd') : '')}
    />
  );
}

function toggleMultiOption(draft: AskUserDraft | undefined, option: AskUserOption): AskUserDraft {
  const current = draft?.kind === 'multiOption' ? draft.options : [];
  const isSame = (other: AskUserOption) => other.id === option.id && other.source === option.source;
  const options = current.some(isSame)
    ? current.filter(other => !isSame(other))
    : [...current, option];
  return { kind: 'multiOption', options };
}

function getIsDraftAnswered(draft: AskUserDraft | undefined) {
  if (draft?.kind === 'option') return true;
  if (draft?.kind === 'multiOption') return draft.options.length > 0;
  if (draft?.kind === 'file') return draft.files.length > 0;
  return draft?.kind === 'text' && Boolean(draft.text.trim());
}
